import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Cookie, X } from 'lucide-react';
import { Button } from '../ui/button';

const CookieBanner = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true,
    analytics: false,
  });

  useEffect(() => {
    const consent = localStorage.getItem('cookieConsent');
    if (!consent) {
      // Show banner after a short delay
      const timer = setTimeout(() => setIsVisible(true), 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAcceptAll = () => {
    const consentData = {
      necessary: true,
      analytics: true,
      timestamp: new Date().toISOString(),
    };
    localStorage.setItem('cookieConsent', JSON.stringify(consentData));
    setIsVisible(false);
  };

  const handleRejectNonNecessary = () => {
    const consentData = {
      necessary: true,
      analytics: false,
      timestamp: new Date().toISOString(),
    };
    localStorage.setItem('cookieConsent', JSON.stringify(consentData));
    setIsVisible(false);
  };

  const handleSavePreferences = () => {
    const consentData = {
      ...preferences,
      timestamp: new Date().toISOString(),
    };
    localStorage.setItem('cookieConsent', JSON.stringify(consentData));
    setIsVisible(false);
    setShowSettings(false);
  };

  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 z-[200] flex items-end sm:items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="p-6 border-b border-slate-100">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
                <Cookie className="w-6 h-6 text-amber-600" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-slate-800">Cookie</h2>
                <p className="text-sm text-slate-500">Gestione preferenze</p>
              </div>
            </div>
            <button
              onClick={handleRejectNonNecessary}
              className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
              aria-label="Chiudi"
            >
              <X className="w-5 h-5 text-slate-400" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {!showSettings ? (
            <>
              <p className="text-slate-600 leading-relaxed mb-6">
                Utilizziamo cookie tecnici necessari al funzionamento del sito e, previo tuo consenso, 
                cookie di analisi per migliorare l'esperienza di navigazione. Puoi accettare tutti i cookie, 
                rifiutare quelli non necessari o personalizzare le scelte. Per saperne di più leggi la nostra{' '}
                <Link to="/cookie-policy" className="text-amber-600 hover:underline font-medium" onClick={() => setIsVisible(false)}>
                  Cookie Policy
                </Link>.
              </p>
            </>
          ) : (
            <div className="space-y-4 mb-6">
              {/* Necessary Cookies */}
              <div className="p-4 bg-slate-50 rounded-xl">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-semibold text-slate-800">Cookie tecnici (necessari)</h3>
                  <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full">Sempre attivi</span>
                </div>
                <p className="text-sm text-slate-600">
                  Necessari al funzionamento del sito. Non possono essere disabilitati.
                </p>
              </div>

              {/* Analytics Cookies */}
              <div className="p-4 bg-slate-50 rounded-xl">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-semibold text-slate-800">Cookie di analisi (facoltativi)</h3>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={preferences.analytics}
                      onChange={(e) => setPreferences({ ...preferences, analytics: e.target.checked })}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-slate-300 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-amber-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-amber-600"></div>
                  </label>
                </div>
                <p className="text-sm text-slate-600">
                  Utilizzati per raccogliere informazioni in forma aggregata e anonima sull'utilizzo del sito.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="p-6 bg-slate-50 border-t border-slate-100">
          {!showSettings ? (
            <div className="flex flex-col gap-3">
              <Button
                onClick={handleAcceptAll}
                className="w-full bg-amber-600 hover:bg-amber-700 text-white"
              >
                Accetta tutti
              </Button>
              <div className="grid grid-cols-2 gap-3">
                <Button
                  onClick={handleRejectNonNecessary}
                  variant="outline"
                  className="border-slate-300 text-slate-700 hover:bg-slate-100"
                >
                  Rifiuta non necessari
                </Button>
                <Button
                  onClick={() => setShowSettings(true)}
                  variant="outline"
                  className="border-slate-300 text-slate-700 hover:bg-slate-100"
                >
                  Personalizza
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                onClick={handleSavePreferences}
                className="flex-1 bg-amber-600 hover:bg-amber-700 text-white"
              >
                Salva preferenze
              </Button>
              <Button
                onClick={() => setShowSettings(false)}
                variant="outline"
                className="flex-1 border-slate-300 text-slate-700 hover:bg-slate-100"
              >
                Indietro
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CookieBanner;
